function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import "core-js/modules/es.array.sort.js";
import "core-js/modules/web.dom-collections.iterator.js";
import { RectClipPath } from "@visx/clip-path";
import { localPoint } from "@visx/event";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { withParentSize } from "@visx/responsive";
import { scaleLinear, scaleTime } from "@visx/scale";
import groupBy from "lodash-es/groupBy";
import minBy from "lodash-es/minBy";
import { observer } from "mobx-react";
import { useEffect, useMemo, useState, createElement as _createElement } from "react";
import Styles from "./bottom-dock-chart.scss";
import Legends from "./Legends";
import Tooltip from "./Tooltip";
import { Cursor, Plot, PointsOnMap, XAxis, YAxis } from "./utils";
import { ZoomX } from "./ZoomX";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const CHART_MIN_WIDTH = 110;
const DEFAULT_GRID_COLOR = "#efefef";
const Y_AXIS_NUM_TICKS = 4;
const Y_AXIS_TICK_LABEL_FONT_SIZE = 10;
const _BottomDockChart = observer(_ref => {
  let {
    chartItems,
    xAxis,
    parentWidth = 0,
    width,
    height,
    margin
  } = _ref;
  return /*#__PURE__*/_jsx(Chart, {
    chartItems: chartItems,
    xAxis: xAxis,
    height: height,
    margin: margin,
    width: Math.max(CHART_MIN_WIDTH, width || parentWidth)
  });
});
export const BottomDockChart = withParentSize(_BottomDockChart);
BottomDockChart.displayName = "BottomDockChart";
const DEFAULT_MARGIN = {
  left: 20,
  right: 30,
  top: 10,
  bottom: 50
};
const Chart = observer(_ref2 => {
  let {
    chartItems: propsChartItems,
    xAxis,
    width,
    height,
    margin = DEFAULT_MARGIN
  } = _ref2;
  const [zoomedXScale, setZoomedXScale] = useState(undefined);
  const [mouseCoords, setMouseCoords] = useState(undefined);
  const processedChartItems = useMemo(() => {
    return sortChartItemsByType(propsChartItems).filter(chartItem => chartItem.points.length > 0).map(chartItem => {
      return _objectSpread(_objectSpread({}, chartItem), {}, {
        points: chartItem.points.slice().sort((p1, p2) => +p1.x - +p2.x)
      });
    });
  }, [propsChartItems]);
  const plotHeight = height - margin.top - margin.bottom - Legends.maxHeightPx;
  const yAxes = useMemo(() => {
    const range = [plotHeight, 0];
    const chartItemsByUnit = groupBy(processedChartItems, "units");
    return Object.entries(chartItemsByUnit).map(_ref3 => {
      let [units, chartItems] = _ref3;
      return {
        units: units === "undefined" ? undefined : units,
        scale: scaleLinear({
          domain: calculateDomainY(chartItems),
          range
        }),
        color: chartItems[0].getColor()
      };
    });
  }, [plotHeight, processedChartItems]);

  // We need to consider only the left most Y-axis as its label values appear
  // outside the chart plot area. The labels of inner y-axes appear inside
  // the plot area.
  const estimatedYAxesWidth = useMemo(() => {
    const leftmostYAxis = yAxes[0];
    const maxLabelDigits = Math.max(0, ...leftmostYAxis.scale.ticks(Y_AXIS_NUM_TICKS).map(n => n.toString().length));
    return maxLabelDigits * Y_AXIS_TICK_LABEL_FONT_SIZE;
  }, [yAxes]);
  const plotWidth = width - margin.left - margin.right - estimatedYAxesWidth;
  const adjustedMargin = useMemo(() => _objectSpread(_objectSpread({}, margin), {}, {
    left: margin.left + estimatedYAxesWidth
  }), [estimatedYAxesWidth, margin]);
  const initialXScale = useMemo(() => {
    const params = {
      domain: calculateDomainX(processedChartItems),
      range: [0, plotWidth]
    };
    if (xAxis.scale === "linear") return scaleLinear(params);else return scaleTime(params);
  }, [xAxis, processedChartItems, plotWidth]);
  const xScale = zoomedXScale || initialXScale;
  const initialScales = useMemo(() => processedChartItems.map(c => ({
    x: initialXScale,
    y: yAxes.find(y => y.units === c.units).scale
  })), [processedChartItems, initialXScale, yAxes]);
  const zoomedScales = useMemo(() => processedChartItems.map(c => ({
    x: xScale,
    y: yAxes.find(y => y.units === c.units).scale
  })), [processedChartItems, xScale, yAxes]);
  const pointsNearMouse = useMemo(() => {
    if (!mouseCoords) return [];
    return processedChartItems.map(chartItem => ({
      chartItem,
      point: findNearestPoint(chartItem.points, mouseCoords, xScale, 7)
    })).filter(pointNotUndefined);
  }, [processedChartItems, mouseCoords, xScale]);
  const cursorX = pointsNearMouse.length > 0 ? xScale(pointsNearMouse[0].point.x) : mouseCoords === null || mouseCoords === void 0 ? void 0 : mouseCoords.x;
  const tooltip = useMemo(() => {
    const margin = adjustedMargin;
    const tooltip = {
      items: pointsNearMouse
    };
    if (!mouseCoords || mouseCoords.x < plotWidth * 0.5) {
      tooltip.right = width - (plotWidth + margin.right);
    } else {
      tooltip.left = margin.left;
    }
    tooltip.bottom = height - (margin.top + plotHeight);
    return tooltip;
  }, [adjustedMargin, pointsNearMouse, mouseCoords, width, plotWidth, height, plotHeight]);
  const setMouseCoordsFromEvent = event => {
    const coords = localPoint(event.target.ownerSVGElement || event.target, event);
    if (!coords) return;
    setMouseCoords({
      x: coords.x - adjustedMargin.left,
      y: coords.y - adjustedMargin.top
    });
  };
  useEffect(() => {
    setZoomedXScale(undefined);
  }, [processedChartItems]);
  if (processedChartItems.length === 0) return /*#__PURE__*/_jsx("div", {
    className: Styles.empty,
    children: "No data available"
  });
  return /*#__PURE__*/_jsxs(ZoomX, {
    surface: "#zoomSurface",
    initialScale: initialXScale,
    scaleExtent: [1, Infinity],
    translateExtent: [[0, 0], [Infinity, Infinity]]
    // Wrap setZoomedXScale in a function to ensure React stores the D3 scale function as a value.
    // If passed directly, React treats functions as state updaters, causing zoom to break.
    ,
    onZoom: xScale => setZoomedXScale(() => xScale),
    children: [/*#__PURE__*/_jsx(Legends, {
      width: plotWidth,
      chartItems: processedChartItems
    }), /*#__PURE__*/_jsxs("div", {
      style: {
        position: "relative"
      },
      children: [/*#__PURE__*/_jsx("svg", {
        width: "100%",
        height: height,
        onMouseMove: setMouseCoordsFromEvent,
        onMouseLeave: () => setMouseCoords(undefined),
        children: /*#__PURE__*/_jsxs(Group, {
          left: adjustedMargin.left,
          top: adjustedMargin.top,
          children: [/*#__PURE__*/_jsx(RectClipPath, {
            id: "plotClip",
            width: plotWidth,
            height: plotHeight
          }), /*#__PURE__*/_jsx(XAxis, {
            top: plotHeight + 1,
            scale: xScale,
            label: xAxis.units || (xAxis.scale === "time" ? "Date" : "")
          }), yAxes.map((y, i) => /*#__PURE__*/_createElement(YAxis, _objectSpread(_objectSpread({}, y), {}, {
            key: "y-axis-".concat(y.units),
            color: yAxes.length > 1 ? y.color : DEFAULT_GRID_COLOR,
            offset: i * 50
          }))), yAxes.map(y => /*#__PURE__*/_jsx(GridRows, {
            width: plotWidth,
            height: plotHeight,
            scale: y.scale,
            numTicks: Y_AXIS_NUM_TICKS,
            stroke: yAxes.length > 1 ? y.color : DEFAULT_GRID_COLOR,
            lineStyle: {
              opacity: 0.3
            }
          }, "grid-".concat(y.units))), /*#__PURE__*/_jsxs("svg", {
            id: "zoomSurface",
            clipPath: "url(#plotClip)",
            pointerEvents: "all",
            children: [/*#__PURE__*/_jsx("rect", {
              width: plotWidth,
              height: plotHeight,
              fill: "transparent"
            }), cursorX && /*#__PURE__*/_jsx(Cursor, {
              x: cursorX,
              stroke: DEFAULT_GRID_COLOR
            }), /*#__PURE__*/_jsx(Plot, {
              chartItems: processedChartItems,
              initialScales: initialScales,
              zoomedScales: zoomedScales
            })]
          })]
        })
      }), /*#__PURE__*/_jsx(Tooltip, _objectSpread({}, tooltip)), /*#__PURE__*/_jsx(PointsOnMap, {
        chartItems: processedChartItems
      })]
    })]
  });
});
Chart.displayName = "Chart";

// Type guard to filter ChartItems that don't produce a nearestPoint
const pointNotUndefined = itemPoint => itemPoint.point !== undefined;

/**
 * Sorts chartItems so that `momentPoints` are rendered on top then
 * `momentLines` and then any other types.
 * @param {ChartItem[]} chartItems array of chartItems to sort
 */
const sortChartItemsByType = chartItems => {
  return chartItems.slice().sort((a, b) => {
    if (a.type === "momentPoints") return 1;else if (b.type === "momentPoints") return -1;else if (a.type === "momentLines") return 1;else if (b.type === "momentLines") return -1;
    return 0;
  });
};

/**
 * Calculates a combined domain of all chartItems.
 * Convert Dates to numbers
 */
const calculateDomainX = chartItems => {
  const xmin = Math.min(...chartItems.map(c => +c.domain.x[0]));
  const xmax = Math.max(...chartItems.map(c => +c.domain.x[1]));
  return [xmin, xmax];
};
const calculateDomainY = chartItems => {
  const ymin = Math.min(...chartItems.map(c => c.domain.y[0]));
  const ymax = Math.max(...chartItems.map(c => c.domain.y[1]));
  return [ymin, ymax];
};
const findNearestPoint = (points, coords, xScale, maxDistancePx) => {
  function distance(coords, point) {
    // Works with numbers or Dates
    return point ? +coords.x - +xScale(point.x) : Infinity;
  }
  let left = 0;
  let right = points.length;
  let mid = 0;
  while (left !== right) {
    mid = left + Math.floor((right - left) / 2);
    const dist = distance(coords, points[mid]);
    if (dist === 0) {
      break;
    } else if (dist < 0) {
      right = mid;
    } else {
      left = mid + 1;
    }
  }
  const leftPoint = points[mid - 1];
  const midPoint = points[mid];
  const rightPoint = points[mid + 1];
  const nearestPoint = minBy([leftPoint, midPoint, rightPoint], p => p ? Math.abs(distance(coords, p)) : Infinity);
  return nearestPoint !== undefined && Math.abs(distance(coords, nearestPoint)) <= maxDistancePx ? nearestPoint : undefined;
};